import React, { useEffect, useState } from "react";
import { Button, Form, Header, Input, Message, Segment, Dropdown } from "semantic-ui-react";
import { toast } from "react-toastify";
import { ToasterConfig } from "../../../config/ToasterConfig";
import useJustificationService from "../../../services/backoffice/hooks/useJustificationService";
import { CreateJustificationCommand, UpdateJustificationCommand } from "../../../models/requests/create-justification-command";
import { JustificationDto } from "../../../models/requests/justification-dto";
import {EJustificationType} from "../../../models/requests/e-justification-type";

interface JustificationFormProps {
    justificationId?: string;
    onClose?: () => void;
    isModal?: boolean;
    open?: boolean;
}

const JustificationForm: React.FC<JustificationFormProps> = ({ justificationId, onClose }) => {
    const [justification, setJustification] = useState<Partial<CreateJustificationCommand>>({ type: EJustificationType.Cancellation });
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const { createJustification, updateJustification, getJustificationById } = useJustificationService();

    const justificationTypeOptions = [
        { key: "cancellation", text: "Cancelamento", value: EJustificationType.Cancellation },
        { key: "modification", text: "Modificação", value: EJustificationType.Modification }
    ];

    useEffect(() => {
        if (justificationId) {
            setLoading(true);
            getJustificationById(justificationId)
                .then((response) => {
                    setJustification({
                        description: response.description,
                        code: response.code,
                        type: response.type
                    });
                })
                .catch(() => {
                    setError("Erro ao carregar a justificativa.");
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [justificationId]);

    const handleSubmit = async () => {
        try {
            if (!justification.code || !justification.description) {
                setError("Todos os campos são obrigatórios.");
                return;
            }

            setLoading(true);
            const command: CreateJustificationCommand | UpdateJustificationCommand = {
                description: justification.description,
                code: justification.code,
                type: justification.type || EJustificationType.Cancellation
            };

            if (justificationId) {
                await updateJustification(justificationId, command as UpdateJustificationCommand);
                toast.success("Justificativa atualizada com sucesso", ToasterConfig.success);
            } else {
                await createJustification(command);
                toast.success("Justificativa criada com sucesso", ToasterConfig.success);
            }

            if (onClose) onClose();
        } catch {
            setError("Erro ao salvar a justificativa.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setJustification((prev) => ({ ...prev, [name]: value }));
    };

    const handleDropdownChange = (event: React.SyntheticEvent, data: any) => {
        setJustification((prev) => ({ ...prev, type: data.value }));
    };

    const handleClear = () => {
        setJustification({ type: EJustificationType.Cancellation });
    };

    const isSubmitDisabled = !justification.code || !justification.description;

    return (
        <Segment raised style={{ maxWidth: "600px", margin: "5px auto", padding: "20px" }}>
            <Header
                as="h3"
                style={{
                    background: "#49ab3a",
                    color: "#fff",
                    display: "flex",
                    alignItems: "left",
                    justifyContent: "space-between",
                    padding: "10px",
                    margin: "0",
                    borderRadius: "5px 5px 0 0",
                }}
            >
                {justificationId ? "Editar Justificativa" : "Cadastro de Justificativa"}
            </Header>
            <Form onSubmit={handleSubmit}>
                <div style={{ border: "2px solid #d4d4d5", borderRadius: "4px", padding: "10px", margin: "10px 0" }}>
                    <Form.Group widths="equal">
                        <Form.Field required>
                            <label>Código</label>
                            <Input
                                name="code"
                                placeholder="Digite o código"
                                value={justification.code || ""}
                                onChange={handleInputChange}
                                disabled={loading}
                                style={{ padding: "10px", width: "100%" }}
                                fluid
                                error={isSubmitDisabled && !justification.code}
                            />
                        </Form.Field>
                        <Form.Field required>
                            <label>Descrição</label>
                            <Input
                                name="description"
                                placeholder="Digite a descrição"
                                value={justification.description || ""}
                                onChange={handleInputChange}
                                disabled={loading}
                                style={{ padding: "10px", width: "100%" }}
                                fluid
                                error={isSubmitDisabled && !justification.description}
                            />
                        </Form.Field>
                        <Form.Field required>
                            <label>Tipo de Justificativa</label>
                            <Dropdown
                                name="type"
                                options={justificationTypeOptions}
                                value={justification.type}
                                onChange={handleDropdownChange}
                                disabled={loading}
                                placeholder="Selecione o tipo de justificativa"
                                selection
                                fluid
                            />
                        </Form.Field>
                    </Form.Group>
                </div>
                <div style={{ textAlign: "right" }}>
                    <Button color="green" type="submit" disabled={isSubmitDisabled || loading}>
                        {justificationId ? "Atualizar" : "Salvar"}
                    </Button>
                    <Button type="button" onClick={handleClear} disabled={loading}>
                        Limpar
                    </Button>
                    <Button type="button" onClick={onClose} disabled={loading}>
                        Cancelar
                    </Button>
                </div>
                {error && <Message negative>{error}</Message>}
            </Form>
        </Segment>
    );
};

export default JustificationForm;
